import React, { useState } from 'react';
import { FolderOpen, Plus, GitBranch, Check, Loader, Lock, AlertCircle, Settings } from 'lucide-react';
import { useDashboard } from '../DashboardContext';
import PlanManage from './PlanManage';

const chip = { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '4px 10px', borderRadius: 4, fontFamily: "'IBM Plex Mono', monospace" };

const STATUS = {
  saving:   { icon: Loader,      text: 'Saving…',    color: '#737373', bg: '#F5F5F5' },
  saved:    { icon: Check,       text: 'Saved',      color: '#15803D', bg: '#F0FDF4' },
  readonly: { icon: Lock,        text: 'Benchmark. Edits become your own option', color: '#92700C', bg: '#FEF9EC' },
  error:    { icon: AlertCircle, text: 'Save failed', color: '#DC2626', bg: '#FEF2F2' },
};

export default function PlanBar() {
  const { planMgr } = useDashboard();
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const [managing, setManaging] = useState(null); // plan being renamed/deleted
  if (!planMgr?.online) return null;

  const { isClient, clients, activeClientId, setActiveClientId, plans, activePlan,
    openPlan, saveNewPlan, forkActivePlan, saveStatus } = planMgr;

  const status = saveStatus && STATUS[saveStatus];
  const selectStyle = { background: '#FFFFFF', border: '1px solid #D4D4D4', borderRadius: 4, padding: '6px 10px', fontSize: 13, color: '#1A1A1A', cursor: 'pointer', maxWidth: 220 };

  const submitName = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    await saveNewPlan(trimmed);
    setNaming(false); setName('');
  };

  return (
    <div className="no-print flex items-center gap-3 flex-wrap mb-4 px-4 py-3" style={{ background: '#FFFFFF', border: '1px solid #D4D4D4', borderRadius: 8 }}>
      <FolderOpen className="w-4 h-4" style={{ color: '#92700C' }} />

      {!isClient && (
        <select value={activeClientId ?? ''} onChange={e => setActiveClientId(Number(e.target.value) || null)} style={selectStyle} aria-label="Client">
          {!clients.length && <option value="">No clients yet. Add one in Clients</option>}
          {clients.map(c => <option key={c.id} value={c.id}>{c.display_name || c.username}</option>)}
        </select>
      )}

      <select
        value={activePlan?.id ?? ''}
        onChange={e => { const p = plans.find(pl => pl.id === Number(e.target.value)); if (p) openPlan(p); }}
        style={selectStyle} aria-label="Plan"
      >
        <option value="">{plans.length ? 'Open a plan…' : 'No saved plans'}</option>
        {plans.map(p => (
          <option key={p.id} value={p.id}>
            {p.created_by_role === 'advisor' ? '★ ' : ''}{p.name}
          </option>
        ))}
      </select>

      {naming ? (
        <span className="flex items-center gap-2">
          <input autoFocus value={name} onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') submitName(); if (e.key === 'Escape') setNaming(false); }}
            placeholder="Plan name" style={{ ...selectStyle, cursor: 'text', width: 180 }} />
          <button onClick={submitName} style={{ ...chip, background: '#1A1A1A', color: '#FFF', border: 'none', cursor: 'pointer' }}>Save</button>
        </span>
      ) : (
        <button onClick={() => setNaming(true)} disabled={!isClient && activeClientId == null}
          style={{ ...chip, background: '#F5F5F5', border: '1px solid #D4D4D4', color: '#1A1A1A', cursor: 'pointer' }}>
          <Plus className="w-3.5 h-3.5" /> Save as new plan
        </button>
      )}

      {activePlan && planMgr.canEditPlan(activePlan) && (
        <button onClick={() => setManaging(activePlan)} title="Rename or delete this plan"
          style={{ ...chip, background: '#F5F5F5', border: '1px solid #D4D4D4', color: '#525252', cursor: 'pointer' }}>
          <Settings className="w-3.5 h-3.5" /> Manage
        </button>
      )}

      {isClient && activePlan && saveStatus === 'readonly' && (
        <button onClick={() => forkActivePlan(planMgr.nextOptionName())} style={{ ...chip, background: '#92700C', color: '#FFF', border: 'none', cursor: 'pointer' }}>
          <GitBranch className="w-3.5 h-3.5" /> Create my own option
        </button>
      )}

      <span style={{ flex: 1 }} />
      {activePlan && <span style={{ fontSize: 12, color: '#525252' }}>{activePlan.created_by_role === 'advisor' ? 'MinerOps plan: ' : ''}{activePlan.name}</span>}
      {status && (
        <span style={{ ...chip, background: status.bg, color: status.color }}>
          <status.icon className="w-3.5 h-3.5" /> {status.text}
        </span>
      )}

      {managing && <PlanManage plan={managing} onClose={() => setManaging(null)} />}
    </div>
  );
}
